var _a;
import { __assign, __values } from "tslib";
import { SecondaryM365ContentIdName } from '@metaos/hub-sdk';
import { ActionIntent, } from '@office-start/action-types';
import { OfficeActionCreators } from '@office-start/actions-service';
import { ActionContextMenuItemIcon, ContentActionGroup, } from '@office-start/document-types';
import { getFileTypeFromExtension } from '@office-start/file-utility';
import { isSupportedActionType } from './mosAppActionGates';
import { isOpenDialogAction, isOpenPageAction, isOpenURLAction, isUrlDialog, } from './mosAppActionTypeGuard';
// Generate query params for Harmony action.
// Update MosAppQueryParams enum in useOfficeExperienceRouter.ts when adding more queryParams, to make sure all added query params are removed when navigate away from current route.
var generateQueryParams = function (contentAction, content) {
    var _a;
    var queryParams = {
        actionId: contentAction.id,
        itemId: content.docId,
    };
    queryParams[SecondaryM365ContentIdName.DriveId] = content.driveId;
    queryParams[SecondaryM365ContentIdName.SiteId] =
        (_a = content.sharepointIds) === null || _a === void 0 ? void 0 : _a.siteId;
    return queryParams;
};
var openUrlAction = function (props) {
    if (!isOpenURLAction(props.supportedHandler)) {
        return undefined;
    }
    if (props.supportedHandler.url) {
        // TODO: added query params to url when design is finalized
        return OfficeActionCreators.createLaunchUrlAction(props.supportedHandler.url, '_blank', {
            referral: props.appId,
            referralPlatform: props.platform,
        });
    }
    return undefined;
};
var notImplemented = function () {
    return undefined;
};
var openPageAction = function (props) {
    if (!isOpenPageAction(props.supportedHandler)) {
        return undefined;
    }
    var queryParams = generateQueryParams(props.contentAction, props.content);
    var pageInfo = props.supportedHandler.pageInfo;
    if (pageInfo.pageId) {
        return OfficeActionCreators.createNavigateToApp(props.appId, pageInfo.pageId, pageInfo.subPageId, {
            referral: props.appId,
            referralPlatform: props.platform,
        }, queryParams);
    }
    return undefined;
};
var openDialogAction = function (props) {
    if (!isOpenDialogAction(props.supportedHandler)) {
        return undefined;
    }
    var queryParams = generateQueryParams(props.contentAction, props.content);
    var dialogInfo = props.supportedHandler.dialogInfo;
    if (isUrlDialog(dialogInfo)) {
        return OfficeActionCreators.createOpenTaskModuleAction(props.appId, dialogInfo.url, dialogInfo.height, dialogInfo.width, queryParams, {
            referral: props.appId,
            referralPlatform: props.platform,
        });
    }
    return undefined;
};
// Check if an action is appicable to a content type
var isContentTypeSupported = function (ationHandler, contentType) {
    var e_1, _a;
    var _b, _c;
    var supporteExtensions = (_c = (_b = ationHandler.supportedObjects) === null || _b === void 0 ? void 0 : _b.file) === null || _c === void 0 ? void 0 : _c.extensions;
    if (supporteExtensions && supporteExtensions.length > 0) {
        try {
            for (var supporteExtensions_1 = __values(supporteExtensions), supporteExtensions_1_1 = supporteExtensions_1.next(); !supporteExtensions_1_1.done; supporteExtensions_1_1 = supporteExtensions_1.next()) {
                var extension = supporteExtensions_1_1.value;
                var supportedType = getFileTypeFromExtension(extension);
                if (contentType === supportedType) {
                    return true;
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (supporteExtensions_1_1 && !supporteExtensions_1_1.done && (_a = supporteExtensions_1.return)) _a.call(supporteExtensions_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
    }
    return false;
};
var mosToHarmonyActionLookupTable = (_a = {},
    _a['invokeAPI'] = notImplemented,
    _a['openURL'] = openUrlAction,
    _a['openDialog'] = openDialogAction,
    _a['openPage'] = openPageAction,
    _a['openTaskpane'] = notImplemented,
    _a['invokeBot'] = notImplemented,
    _a);
// Fore each action, Mos app can define multiple handlers in manifest. This function returns the first handler that is supported by Harmony.
var getFirstSupportedHandler = function (actionHandlers) {
    var e_2, _a;
    try {
        for (var actionHandlers_1 = __values(actionHandlers), actionHandlers_1_1 = actionHandlers_1.next(); !actionHandlers_1_1.done; actionHandlers_1_1 = actionHandlers_1.next()) {
            var actionHandler = actionHandlers_1_1.value;
            if (isSupportedActionType(actionHandler.type)) {
                return actionHandler;
            }
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (actionHandlers_1_1 && !actionHandlers_1_1.done && (_a = actionHandlers_1.return)) _a.call(actionHandlers_1);
        }
        finally { if (e_2) throw e_2.error; }
    }
    return undefined;
};
/**
 * Convert mos app action defined in manifest to Harmony action handler
 * For one intent, mulptile handler could be defined in manifest. Hub will conver the first supported handler to Harmony action handler
 * In addition, hub only create action for content that matches the handler's content type
 */
export var convertToHarmonyAction = function (props) {
    try {
        var contentAction = props.contentAction, content = props.content;
        var supportedHandler = getFirstSupportedHandler(contentAction.handlers);
        if (supportedHandler &&
            content.fileType &&
            isContentTypeSupported(supportedHandler, content.fileType)) {
            return mosToHarmonyActionLookupTable[supportedHandler.type](__assign(__assign({}, props), { supportedHandler: supportedHandler }));
        }
    }
    catch (error) {
        // TODO: console.log is used for private preview. Should be removed then
        // TODO: add formal logging.
        console.log('Failed to convert action from manifest to harmony');
        console.log(error);
    }
    return undefined;
};
// Convert manifest action intent to Harmony action intent
export var convertToHarmonyIntent = function (intentFromManifest) {
    // TODO: finalize mapping
    switch (intentFromManifest) {
        case 'create':
            return ActionIntent.ContentCreate;
        case 'custom':
            return ActionIntent.ContentEdit;
        default:
            return ActionIntent.ContentView;
    }
};
// Convert manifest action intent to Harmony action group
export var convertToHarmonyActionGroup = function (intentFromManifest) {
    // PrivatePreview support Open, AddTo and Custom intents only. The rest will default to same group as Custom.
    switch (intentFromManifest) {
        case 'addTo':
            return ContentActionGroup.AddTo;
        case 'open':
            return ContentActionGroup.Open;
        default:
            return ContentActionGroup.MosApp;
    }
};
// Convert manifest action icon to Harmony action icon
export var convertToHarmonyIcon = function (_contentAction, appIconUrl) {
    // Use app icon in context menu for now.
    // TODO:  revisit when design finalized.
    var icon = {
        type: ActionContextMenuItemIcon.Url,
        iconUrl: appIconUrl,
    };
    return icon;
};
/**
 * Generate unique actionId in Harmonoy by pfefixing appId to actionId defined in manifest
 * @param appId Mos app Id
 * @param actionIdFromManifest actionId defined in manifest
 * @returns unique actionId in Harmony
 */
export var generateHarmonyActionId = function (appId, actionIdFromManifest) {
    return "MosApp_".concat(appId, "_").concat(actionIdFromManifest);
};
//# sourceMappingURL=mosAppActionUtils.js.map